import * as React from 'react'
import { Link } from 'react-router-dom'
import { toast } from '@/toast/toastStore'
import apiService from '@/services/api'
import { Button } from '@/components/ui/Button'
import { AuthShell } from '@/components/auth/AuthShell'
import { FormField } from '@/components/auth/FormField'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Sottovo パスワード忘れページ (`/auth/forgot-password`)。
 *
 * 既存 ForgotPasswordPage の挙動をそのまま踏襲する:
 *   - apiService.forgotPassword(email) を呼ぶ
 *   - 成功時 → 送信完了ビュー (sent state) に切り替え
 *   - 失敗時 → antd message でエラー表示
 *
 * UI のみ v2 ブランドで再構築。
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = React.useState('')
  const [error, setError] = React.useState<string | undefined>(undefined)
  const [loading, setLoading] = React.useState(false)
  const [sent, setSent] = React.useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    let validationError: string | undefined
    if (!email) validationError = 'メールアドレスを入力してください'
    else if (!EMAIL_RE.test(email))
      validationError = '有効なメールアドレスを入力してください'
    setError(validationError)
    if (validationError) return

    setLoading(true)
    try {
      const response = await apiService.forgotPassword(email)
      if (response.success) {
        setSent(true)
        toast.success('パスワードリセットメールを送信しました')
      } else {
        toast.error(response.error || 'パスワードリセットメールの送信に失敗しました')
      }
    } catch (err) {
      console.error('Password reset error:', err)
      toast.error('パスワードリセットメールの送信に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  if (sent) {
    return (
      <AuthShell
        title="メールを送信しました"
        subtitle="パスワードリセットの手順をメールでお送りしました"
      >
        <p className="text-base leading-relaxed text-v2-ink-muted">
          メールが届かない場合は、迷惑メールフォルダをご確認ください。
          それでも届かない場合は、サポートまでお問い合わせください。
        </p>

        <div className="mt-8">
          <Link
            to="/auth/login"
            className="text-sm font-medium text-v2-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-primary focus-visible:ring-offset-2"
          >
            ← ログインページに戻る
          </Link>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="パスワードを忘れた場合"
      subtitle="登録済みメールアドレスを入力してください"
    >
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        <FormField
          id="forgot-email"
          label="メールアドレス"
          type="email"
          autoComplete="email"
          placeholder="your.email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={error}
        />

        <Button type="submit" size="md" className="w-full" disabled={loading}>
          {loading ? '送信中…' : 'パスワードリセットメールを送信'}
        </Button>
      </form>

      <div className="mt-8">
        <Link
          to="/auth/login"
          className="text-sm font-medium text-v2-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-primary focus-visible:ring-offset-2"
        >
          ← ログインページに戻る
        </Link>
      </div>
    </AuthShell>
  )
}
